'use es6';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { DROP_TARGET_MAIN_COLOR, DROP_TARGET_OUTLINE_COLOR, DROP_TARGET_DISABLED_COLOR } from 'layout-dnd-components/constants';
import { HORIZONTAL, VERTICAL } from 'layout-dnd-utils/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LINE_THICKNESS = 3;
export const BALL_RADIUS = 15;
const DEFAULT_SHADE_THICKNESS = LINE_THICKNESS * 6;
export const OUTLINE_DELTA = 2; // Needs to be even
const OUTLINE_THICKNESS = LINE_THICKNESS + OUTLINE_DELTA;
export const SHADE_ABOVE = 'SHADE_ABOVE';
export const SHADE_BELOW = 'SHADE_BELOW';
export const SHADE_LEFT = 'SHADE_LEFT';
export const SHADE_RIGHT = 'SHADE_RIGHT';
export const SHADE_BOTH = 'SHADE_BOTH';
const calculateOutlineBallRadius = ballRadius => ballRadius + OUTLINE_DELTA;
const CoreInsertTargetStyles = css(["position:absolute;width:100%;border:0;padding:0;pointer-events:none;"]);
const BaseInnerTargetStyles = css(["", ";z-index:2;&::before,&::after{content:' ';border-radius:100%;background-color:", ";width:", "px;height:", "px;display:inline-block;position:absolute;}"], CoreInsertTargetStyles, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_MAIN_COLOR, ({
  ballRadius
}) => ballRadius, ({
  ballRadius
}) => ballRadius);
const HorizontalTransformStyles = css(["", ""], ({
  ballRadius
}) => ballRadius % 2 === 0 ? 'transform: translate(0, 0.5px);' : '');
const VerticalTransformStyles = css(["", ""], ({
  ballRadius
}) => ballRadius % 2 === 0 ? 'transform: translate(0.5px, 0);' : '');

// Note, parent React component is dealing with adjusting top position to place the line correctly
const HorizontalInnerStyledElement = styled.div.withConfig({
  displayName: "InsertDropTargets__HorizontalInnerStyledElement",
  componentId: "yb4njf-0"
})([".hs-private.hs-private.hs-private &{", ";border-top:", "px solid ", ";}.hs-private.hs-private.hs-private &::before,.hs-private.hs-private.hs-private &::after{top:", "px;", ";}.hs-private.hs-private.hs-private &::before{left:", "px;}.hs-private.hs-private.hs-private &::after{right:", "px;}"], BaseInnerTargetStyles, LINE_THICKNESS, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_MAIN_COLOR, ({
  ballRadius
}) => -1 * (ballRadius / 2) - 1.5, HorizontalTransformStyles, ({
  ballRadius
}) => -1 * (ballRadius / 2), ({
  ballRadius
}) => -1 * (ballRadius / 2));

// Note, parent React component is dealing with adjusting top position to place the line correctly
const VerticalInnerStyledElement = styled.div.withConfig({
  displayName: "InsertDropTargets__VerticalInnerStyledElement",
  componentId: "yb4njf-1"
})([".hs-private.hs-private.hs-private &{", ";border-left:", "px solid ", ";}.hs-private.hs-private.hs-private &::before,.hs-private.hs-private.hs-private &::after{left:", "px;", ";}.hs-private.hs-private.hs-private &::before{top:", "px;}.hs-private.hs-private.hs-private &::after{bottom:", "px;}"], BaseInnerTargetStyles, LINE_THICKNESS, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_MAIN_COLOR, ({
  ballRadius
}) => -1 * (ballRadius / 2) - 1.5, VerticalTransformStyles, ({
  ballRadius
}) => -1 * (ballRadius / 2), ({
  ballRadius
}) => -1 * (ballRadius / 2));
const BaseOutlineTargetStyles = css(["", ";z-index:2;&::before,&::after{content:' ';border-radius:100%;background-color:", ";width:", "px;height:", "px;display:inline-block;position:absolute;}"], CoreInsertTargetStyles, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_OUTLINE_COLOR, ({
  ballRadius
}) => calculateOutlineBallRadius(ballRadius), ({
  ballRadius
}) => calculateOutlineBallRadius(ballRadius));

// Note, parent React component is dealing with adjusting top position to place the line correctly
const HorizontalOutlineStyledElement = styled.div.withConfig({
  displayName: "InsertDropTargets__HorizontalOutlineStyledElement",
  componentId: "yb4njf-2"
})([".hs-private.hs-private.hs-private &{", ";border-top:", "px solid ", ";}.hs-private.hs-private.hs-private &::before,.hs-private.hs-private.hs-private &::after{top:", "px;", ";}.hs-private.hs-private.hs-private &::before{left:", "px;}.hs-private.hs-private.hs-private &::after{right:", "px;}"], BaseOutlineTargetStyles, OUTLINE_THICKNESS, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_OUTLINE_COLOR, ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2) - OUTLINE_DELTA / 2 - 1.5, HorizontalTransformStyles, ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2), ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2));

// Note, parent React component is dealing with adjusting top position to place the line correctly
const VerticalOutlineStyledElement = styled.div.withConfig({
  displayName: "InsertDropTargets__VerticalOutlineStyledElement",
  componentId: "yb4njf-3"
})([".hs-private.hs-private.hs-private &{", ";border-left:", "px solid ", ";}.hs-private.hs-private.hs-private &::before,.hs-private.hs-private.hs-private &::after{left:", "px;", ";}.hs-private.hs-private.hs-private &::before{top:", "px;}.hs-private.hs-private.hs-private &::after{bottom:", "px;}"], BaseOutlineTargetStyles, OUTLINE_THICKNESS, ({
  disabled
}) => disabled ? DROP_TARGET_DISABLED_COLOR : DROP_TARGET_OUTLINE_COLOR, ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2) - OUTLINE_DELTA / 2 - 1.5, VerticalTransformStyles, ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2), ({
  ballRadius
}) => -1 * (calculateOutlineBallRadius(ballRadius) / 2));
function componentsForOrientation(orientation) {
  if (orientation === HORIZONTAL) {
    return {
      InnerComponent: HorizontalInnerStyledElement,
      OuterComponent: HorizontalOutlineStyledElement
    };
  }
  return {
    InnerComponent: VerticalInnerStyledElement,
    OuterComponent: VerticalOutlineStyledElement
  };
}
const BaseShadeStyles = css(["", ";z-index:1;background-color:", ";opacity:0.5;"], CoreInsertTargetStyles, DROP_TARGET_MAIN_COLOR);
const Shade = styled.div.withConfig({
  displayName: "InsertDropTargets__Shade",
  componentId: "yb4njf-4"
})([".hs-private.hs-private.hs-private &{", ";}"], BaseShadeStyles);
const InsertDropTarget = ({
  top,
  left,
  outlineTop,
  outlineLeft,
  width,
  height,
  orientation,
  shade,
  shadeThickness,
  ballRadius,
  disabled
}) => {
  const {
    InnerComponent,
    OuterComponent
  } = componentsForOrientation(orientation);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(OuterComponent, {
      style: {
        top: outlineTop != null ? outlineTop : top,
        left: outlineLeft != null ? outlineLeft : left,
        width,
        height
      },
      disabled: disabled,
      ballRadius: ballRadius
    }), /*#__PURE__*/_jsx(InnerComponent, {
      style: {
        top,
        left,
        width,
        height
      },
      disabled: disabled,
      ballRadius: ballRadius
    }), (shade === SHADE_ABOVE || shade === SHADE_BOTH && orientation === HORIZONTAL) && /*#__PURE__*/_jsx(Shade, {
      style: {
        top: top - shadeThickness + LINE_THICKNESS / 2,
        left,
        width,
        height: shadeThickness
      },
      "data-test-id": shade,
      disabled: disabled
    }), (shade === SHADE_BELOW || shade === SHADE_BOTH && orientation === HORIZONTAL) && /*#__PURE__*/_jsx(Shade, {
      style: {
        top: top + 1 + LINE_THICKNESS / 2,
        left,
        width,
        height: shadeThickness
      },
      "data-test-id": shade,
      disabled: disabled
    }), (shade === SHADE_LEFT || shade === SHADE_BOTH && orientation === VERTICAL) && /*#__PURE__*/_jsx(Shade, {
      style: {
        top,
        left: left - shadeThickness + LINE_THICKNESS / 2,
        width: shadeThickness,
        height
      },
      "data-test-id": shade,
      disabled: disabled
    }), (shade === SHADE_RIGHT || shade === SHADE_BOTH && orientation === VERTICAL) && /*#__PURE__*/_jsx(Shade, {
      style: {
        top,
        left: left + 1 + LINE_THICKNESS / 2,
        width: shadeThickness,
        height
      },
      "data-test-id": shade,
      disabled: disabled
    })]
  });
};
InsertDropTarget.propTypes = {
  top: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  outlineTop: PropTypes.number,
  outlineLeft: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shade: PropTypes.oneOf([SHADE_ABOVE, SHADE_BELOW, SHADE_LEFT, SHADE_RIGHT, SHADE_BOTH]),
  shadeThickness: PropTypes.number.isRequired,
  orientation: PropTypes.oneOf([HORIZONTAL, VERTICAL]).isRequired,
  ballRadius: PropTypes.number.isRequired,
  disabled: PropTypes.bool
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
InsertDropTarget.defaultProps = {
  shadeThickness: DEFAULT_SHADE_THICKNESS
};
export const HorizontalInsertDropTarget = ({
  top,
  left,
  width,
  shade,
  shadeThickness,
  ballRadius = BALL_RADIUS,
  disabled = false
}) => {
  return /*#__PURE__*/_jsx(InsertDropTarget, {
    top: top - 1.5,
    outlineTop: top - OUTLINE_DELTA / 2 - 1.5,
    left: left,
    width: width,
    shade: shade,
    shadeThickness: shadeThickness,
    orientation: HORIZONTAL,
    ballRadius: ballRadius,
    disabled: disabled
  });
};
export const VerticalInsertDropTarget = ({
  top,
  left,
  height,
  shade,
  shadeThickness,
  ballRadius = BALL_RADIUS,
  disabled = false
}) => {
  return /*#__PURE__*/_jsx(InsertDropTarget, {
    top: top,
    left: left - 1.5,
    outlineLeft: left - OUTLINE_DELTA / 2 - 1.5,
    height: height,
    shade: shade,
    shadeThickness: shadeThickness,
    orientation: VERTICAL,
    ballRadius: ballRadius,
    disabled: disabled
  });
};